import {
  ADVISORY_DATA_REVIEWED_AT,
  NO_EVIDENCE_SUMMARY,
  SUPPORTED_FIXED_VERSION_LIST,
} from './advisory';
import {
  CLASSIFICATION_LABELS,
  SEVERITY_LABELS,
  severityFor,
  type Classification,
  type EvidenceStatus,
  type Severity,
} from './labels';
import { SAFE_MESSAGES } from './messages';
import type { EvidenceItem, Finding, PhpIndicator } from './schema';
import {
  extractVersionsFromHeader,
  selectDrivingAssessment,
  type PhpVersion,
  type VersionAssessment,
} from './versions';

export interface HeaderBag {
  [name: string]: string | string[] | undefined;
}

export interface TechnologyNote {
  name: string;
  detail: string;
}

export interface HeaderAnalysis {
  versions: PhpVersion[];
  productWithoutVersion: boolean;
  weakCookie: boolean;
  indicators: PhpIndicator[];
  technologies: TechnologyNote[];
  evidence: EvidenceItem[];
}

export interface ClassifyInput {
  headerSets: HeaderBag[];
  httpStatus: number | null;
  failureMessage: string | null;
  truncated: boolean;
  unsafeRedirect: boolean;
  tooManyRedirects: boolean;
}

export interface ClassificationBody {
  classification: Classification;
  classificationLabel: string;
  severity: Severity;
  severityLabel: string;
  summary: string;
  detectedPhpVersion: string | null;
  phpBranch: string | null;
  fixedVersion: string | null;
  fixedVersionNote: string | null;
  phpIndicators: PhpIndicator[];
  technologies: TechnologyNote[];
  findings: Finding[];
  evidence: EvidenceItem[];
  limitations: string[];
  remediation: string[];
}

const BASE_LIMITATIONS = [
  'The actual server-side PHP version and installed patches could not be verified from a public HTTP response.',
  'A hosting provider or operating system may backport fixes without changing the visible version string.',
  'This scan sent a normal GET request to the site root only. It did not log in, crawl the site, or test exploit conditions.',
];

function sanitizeVisible(value: string, max = 180): string {
  const cleaned = value.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1)}…`;
}

function valuesFor(headers: HeaderBag, name: string): string[] {
  const entry = Object.entries(headers).find(([key]) => key.toLowerCase() === name);
  if (!entry) return [];
  const raw = entry[1];
  if (raw === undefined) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map((value) => sanitizeVisible(value)).filter(Boolean);
}

function cookieNames(headers: HeaderBag): string[] {
  const setCookies = Object.entries(headers)
    .filter(([key]) => key.toLowerCase() === 'set-cookie')
    .flatMap(([, value]) => (value === undefined ? [] : Array.isArray(value) ? value : [value]));
  return setCookies
    .map((value) => value.split(';', 1)[0]?.split('=', 1)[0]?.trim().toLowerCase() ?? '')
    .filter(Boolean);
}

function hasPhpProductWord(value: string): boolean {
  return /\bphp\b/i.test(value) || /mod_php/i.test(value);
}

function finding(severity: Severity, title: string, detail: string): Finding {
  return { severity, title, detail };
}

function evidence(id: string, title: string, status: EvidenceStatus, detail: string): EvidenceItem {
  return { id, title, status, detail };
}

export function analyzeHeaders(headerSets: HeaderBag[]): HeaderAnalysis {
  const versions: PhpVersion[] = [];
  const indicators: PhpIndicator[] = [];
  const technologies: TechnologyNote[] = [];
  let poweredByValues: string[] = [];
  let serverValues: string[] = [];
  let phpVersionHeader: string[] = [];
  let sawPhpSession = false;

  for (const headers of headerSets) {
    poweredByValues = poweredByValues.concat(valuesFor(headers, 'x-powered-by'));
    serverValues = serverValues.concat(valuesFor(headers, 'server'));
    phpVersionHeader = phpVersionHeader.concat(valuesFor(headers, 'x-php-version'));
    if (cookieNames(headers).includes('phpsessid')) sawPhpSession = true;
  }

  const unique = (items: string[]) => [...new Set(items)];
  poweredByValues = unique(poweredByValues);
  serverValues = unique(serverValues);
  phpVersionHeader = unique(phpVersionHeader);

  for (const value of poweredByValues) {
    versions.push(...extractVersionsFromHeader('x-powered-by', value));
  }
  for (const value of serverValues) {
    versions.push(...extractVersionsFromHeader('server', value));
  }
  for (const value of phpVersionHeader) {
    versions.push(...extractVersionsFromHeader('x-php-version', value));
  }

  const versionKeys = new Set<string>();
  const dedupedVersions = versions.filter((version) => {
    const key = `${version.source}:${version.raw}`;
    if (versionKeys.has(key)) return false;
    versionKeys.add(key);
    return true;
  });

  const explicitSources = new Set(dedupedVersions.map((version) => version.source));
  let productWithoutVersion = false;

  for (const value of poweredByValues) {
    technologies.push({ name: 'X-Powered-By', detail: value });
    if (explicitSources.has('x-powered-by')) continue;
    if (hasPhpProductWord(value)) {
      productWithoutVersion = true;
      indicators.push({
        type: 'header',
        name: 'x-powered-by',
        value,
        strength: 'explicit',
      });
    }
  }

  for (const value of serverValues) {
    technologies.push({ name: 'Server', detail: value });
  }

  for (const value of phpVersionHeader) {
    technologies.push({ name: 'X-PHP-Version', detail: value });
    if (!explicitSources.has('x-php-version')) {
      productWithoutVersion = true;
      indicators.push({
        type: 'header',
        name: 'x-php-version',
        value,
        strength: 'explicit',
      });
    }
  }

  for (const version of dedupedVersions) {
    indicators.push({
      type: 'header',
      name: version.source,
      value: version.raw,
      strength: 'explicit',
    });
  }

  if (sawPhpSession) {
    indicators.push({
      type: 'cookie',
      name: 'PHPSESSID',
      value: null,
      strength: 'weak',
    });
    technologies.push({
      name: 'Cookie',
      detail: 'PHPSESSID was present. The cookie value was not stored.',
    });
  }

  const poweredByPhpVersion = dedupedVersions.some((version) => version.source === 'x-powered-by');
  const serverPhpVersion = dedupedVersions.some((version) => version.source === 'server');
  const headerPhpVersion = dedupedVersions.some((version) => version.source === 'x-php-version');

  const evidenceItems: EvidenceItem[] = [
    evidence(
      'x-powered-by',
      'X-Powered-By header',
      poweredByValues.length === 0
        ? 'not_observable'
        : poweredByPhpVersion || poweredByValues.some(hasPhpProductWord)
          ? 'observed'
          : 'observed',
      poweredByValues.length === 0
        ? 'No X-Powered-By header was present.'
        : poweredByPhpVersion
          ? `X-Powered-By explicitly included ${dedupedVersions
              .filter((version) => version.source === 'x-powered-by')
              .map((version) => version.raw)
              .join(', ')}.`
          : poweredByValues.some(hasPhpProductWord)
            ? `X-Powered-By indicated PHP without a comparable version: ${poweredByValues.join(', ')}.`
            : `X-Powered-By was ${poweredByValues.join(', ')}. It does not identify a PHP version.`,
    ),
    evidence(
      'server-banner',
      'Server banner',
      serverValues.length === 0 ? 'not_observable' : 'observed',
      serverValues.length === 0
        ? 'No Server header was present.'
        : serverPhpVersion
          ? `The Server header explicitly included a PHP version: ${dedupedVersions
              .filter((version) => version.source === 'server')
              .map((version) => version.raw)
              .join(', ')}.`
          : `The Server header was ${serverValues.join(', ')}. It is not treated as a PHP version.`,
    ),
    evidence(
      'php-version-header',
      'Explicit PHP version header',
      headerPhpVersion || phpVersionHeader.length > 0 ? 'observed' : 'not_observable',
      headerPhpVersion
        ? `An explicit PHP version header included ${dedupedVersions
            .filter((version) => version.source === 'x-php-version')
            .map((version) => version.raw)
            .join(', ')}.`
        : phpVersionHeader.length > 0
          ? `A PHP version header was present but did not contain a comparable release: ${phpVersionHeader.join(', ')}.`
          : 'No explicit PHP version header was present.',
    ),
    evidence(
      'php-session-cookie',
      'PHP session cookie',
      sawPhpSession ? 'weak_indicator' : 'not_observable',
      sawPhpSession
        ? 'A Set-Cookie header named PHPSESSID was present. That only weakly suggests PHP, and the cookie value was not stored.'
        : 'A PHP session cookie was not observed.',
    ),
    evidence(
      'patch-status',
      'Installed patch status',
      'not_verified',
      'The scan cannot see the build that actually serves requests, vendor backports, or hidden PHP runtimes.',
    ),
  ];

  return {
    versions: dedupedVersions,
    productWithoutVersion,
    weakCookie: sawPhpSession,
    indicators,
    technologies,
    evidence: evidenceItems,
  };
}

function standardRemediation(fixedVersion: string | null, endOfLife: boolean): string[] {
  const upgradeTarget = fixedVersion
    ? `Upgrade the web-serving PHP runtime to ${fixedVersion} or a later release on a supported branch.`
    : `Upgrade the web-serving PHP runtime to a supported fixed release (${SUPPORTED_FIXED_VERSION_LIST}).`;

  const steps = [
    'Verify the PHP runtime and build that actually serves web requests. Updating the command-line PHP binary does not necessarily update Apache, PHP-FPM, CGI, or an embedded runtime.',
    upgradeTarget,
    'Check the applicable hosting-provider or operating-system advisory for a backported package. A visible version string is not proof that a vendor patch is present or absent.',
    'Confirm that PHP-FPM, Apache, CGI, and any other handler that can serve PHP are running the patched build, then restart those workers.',
    'Review the official PHP advisories for further guidance. A configuration change does not replace the upstream security update.',
    'Retest after remediation and confirm the serving build on the server or with the hosting provider.',
  ];

  if (endOfLife) {
    steps.splice(
      1,
      0,
      'This visible branch is end of life. The PHP project did not publish upstream fixes for PHP 8.1 and earlier. Move to a supported branch unless the vendor documents its own patch.',
    );
  }

  return steps;
}

function inconclusiveRemediation(): string[] {
  return [
    'On the server, identify the PHP build that serves this website, including PHP-FPM, Apache, CGI, and any embedded runtime.',
    `Compare that build with the known upstream fixed releases: ${SUPPORTED_FIXED_VERSION_LIST}. PHP 8.1 and earlier did not receive an upstream fix.`,
    'Ask the hosting provider which package or image is actually in production if the runtime is managed for you.',
    'Review the official PHP advisories before relying on a configuration change. An upstream update is required for a full fix.',
    'Run this assessment again only after you have authorization and the serving runtime has been checked.',
  ];
}

function withCommonLimitations(extra: string[]): string[] {
  return [...extra, ...BASE_LIMITATIONS];
}

function labels(classification: Classification) {
  const severity = severityFor(classification);
  return {
    classification,
    classificationLabel: CLASSIFICATION_LABELS[classification],
    severity,
    severityLabel: SEVERITY_LABELS[severity],
  };
}

function versionFields(assessment: VersionAssessment | null) {
  if (!assessment) {
    return {
      detectedPhpVersion: null,
      phpBranch: null,
      fixedVersion: null,
      fixedVersionNote: null,
    };
  }
  return {
    detectedPhpVersion: assessment.version.raw,
    phpBranch: assessment.branch,
    fixedVersion: assessment.fixedVersion,
    fixedVersionNote: noteFor(assessment),
  };
}

function noteFor(assessment: VersionAssessment): string {
  if (assessment.kind === 'eol') {
    return `PHP ${assessment.branch} is end of life. Upstream did not publish a PHP2Shell fix for this branch. Supported fixed releases are ${SUPPORTED_FIXED_VERSION_LIST}.`;
  }
  if (assessment.kind === 'unlisted') {
    return `PHP ${assessment.branch} is outside the bundled fixed-release table, which was reviewed on ${ADVISORY_DATA_REVIEWED_AT.slice(0, 10)}. Patch status was not classified from that version.`;
  }
  if (assessment.kind === 'prerelease') {
    return `A pre-release marker was observed. It is not treated as the final fixed release${assessment.fixedVersion ? ` ${assessment.fixedVersion}` : ''}.`;
  }
  if (assessment.fixedVersion) {
    return `The known upstream fixed release for PHP ${assessment.branch} is ${assessment.fixedVersion}.`;
  }
  return 'No upstream fixed release was available for this visible version.';
}

function isAnalyzableStatus(status: number | null): boolean {
  if (status === null) return false;
  return (status >= 200 && status < 300) || status === 404;
}

function isBlockedStatus(status: number | null): boolean {
  if (status === null) return false;
  return status === 401 || status === 403 || status === 405 || status === 406 || status === 408 || status === 429 || status === 451 || status >= 500;
}

function transportLimitations(input: ClassifyInput): string[] {
  const extra: string[] = [];
  if (input.truncated) {
    extra.push(
      'The response body exceeded the configured limit and reading stopped. The body was not stored. Header evidence already received was still reviewed.',
    );
  }
  if (input.unsafeRedirect) extra.push(SAFE_MESSAGES.redirectBlocked);
  if (input.tooManyRedirects) extra.push(SAFE_MESSAGES.tooManyRedirects);
  if (input.httpStatus !== null && !isAnalyzableStatus(input.httpStatus) && input.httpStatus >= 300) {
    extra.push(`The final response status was HTTP ${input.httpStatus}.`);
  }
  return extra;
}

export function classifyScan(input: ClassifyInput): ClassificationBody {
  const analysis = analyzeHeaders(input.headerSets);
  const assessment = selectDrivingAssessment(analysis.versions);
  const transport = transportLimitations(input);
  const fields = versionFields(assessment);

  if (assessment && (assessment.kind === 'below' || assessment.kind === 'eol')) {
    const summary =
      assessment.kind === 'eol'
        ? `The response included PHP ${assessment.version.raw}. That branch is end of life, and the PHP project did not publish an upstream fix for PHP 8.1 or earlier. This is evidence of possible PHP2Shell exposure, not proof that a vendor patch is absent.`
        : `The response included PHP ${assessment.version.raw}. That visible version is older than the known upstream fixed release ${assessment.fixedVersion} for the ${assessment.branch} branch. This is evidence of possible exposure, not proof that the server remains vulnerable. Vendor backports and custom builds were not verified.`;
    return {
      ...labels('potentially_vulnerable'),
      summary,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [
        finding(
          'potential_risk',
          'Visible PHP version is behind the known upstream fix',
          summary,
        ),
      ],
      evidence: analysis.evidence,
      limitations: withCommonLimitations(transport),
      remediation: standardRemediation(assessment.fixedVersion, assessment.kind === 'eol'),
    };
  }

  if (assessment && assessment.kind === 'at_or_above') {
    const summary = `The response included PHP ${assessment.version.raw}, which is at or above the known upstream fixed release ${assessment.fixedVersion} for this branch. Version evidence suggests the visible runtime includes the upstream fix. This result is not a guarantee that the server is secure. Other configurations, vendor-specific builds, and hidden PHP runtimes were not verified.`;
    return {
      ...labels('version_suggests_fixed'),
      summary,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [
        finding(
          'likely_patched',
          'Visible version meets the known fixed release',
          summary,
        ),
      ],
      evidence: analysis.evidence,
      limitations: withCommonLimitations([
        'A version at or above the fixed release does not prove every PHP runtime on this host is patched.',
        ...transport,
      ]),
      remediation: [
        `The visible version meets the known fixed line for PHP ${assessment.branch} (${assessment.fixedVersion}). Confirm that this is the runtime answering web requests, not only a shell binary.`,
        'Restart PHP-FPM, Apache, CGI, and any embedded workers after an update so the patched build is the one serving traffic.',
        'Review vendor and operating-system advisories in case this string comes from a custom build.',
        'Read the official PHP advisories. This scanner does not replace them.',
        'Re-check the serving build after any change. This result does not mean the rest of the server was verified.',
      ],
    };
  }

  if (assessment && (assessment.kind === 'prerelease' || assessment.kind === 'unlisted')) {
    const summary =
      assessment.kind === 'prerelease'
        ? `The response included PHP ${assessment.version.raw}, which looks like a pre-release. It was not classified as a final fixed release. Confirm the exact build on the server.`
        : `The response included PHP ${assessment.version.raw}, but that branch is not in the bundled fixed-release table. Patch status was not classified.`;
    return {
      ...labels('inconclusive'),
      summary,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [finding('inconclusive', 'Version seen, patch status not classified', summary)],
      evidence: analysis.evidence,
      limitations: withCommonLimitations([noteFor(assessment), ...transport]),
      remediation: standardRemediation(assessment.fixedVersion, false),
    };
  }

  const phpSignal = analysis.productWithoutVersion || analysis.weakCookie;
  const blocked = isBlockedStatus(input.httpStatus);

  if (blocked && !assessment) {
    const summary = input.httpStatus
      ? `The website returned HTTP ${input.httpStatus}. ${SAFE_MESSAGES.blocked}`
      : SAFE_MESSAGES.blocked;
    return {
      ...labels('inconclusive'),
      summary,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [finding('inconclusive', 'Response was blocked or incomplete', summary)],
      evidence: analysis.evidence,
      limitations: withCommonLimitations([
        phpSignal
          ? 'A PHP indicator was noticed, but the response is not sufficient to judge the runtime version.'
          : 'Headers returned with this status did not provide a reliable PHP version.',
        ...transport,
      ]),
      remediation: inconclusiveRemediation(),
    };
  }

  if ((analysis.productWithoutVersion || analysis.weakCookie) && isAnalyzableStatus(input.httpStatus)) {
    const summary = analysis.productWithoutVersion
      ? 'The response indicates PHP may be in use, but it does not reveal a comparable runtime version. This scan cannot establish whether the serving build includes the PHP2Shell fixes.'
      : 'A PHP session cookie name was observed. That is a weak indicator of PHP and does not reveal a version. This scan cannot establish patch status.';
    return {
      ...labels('php_version_unknown'),
      summary,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [finding('inconclusive', 'PHP indicated, version not visible', summary)],
      evidence: analysis.evidence,
      limitations: withCommonLimitations(transport),
      remediation: standardRemediation(null, false),
    };
  }

  if (input.failureMessage) {
    return {
      ...labels('inconclusive'),
      summary: input.failureMessage,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [finding('inconclusive', 'Scan did not complete', input.failureMessage)],
      evidence: analysis.evidence.length > 0 ? analysis.evidence : [
        evidence('request', 'Public response', 'not_observable', input.failureMessage),
      ],
      limitations: withCommonLimitations(transport.length > 0 ? transport : [input.failureMessage]),
      remediation: inconclusiveRemediation(),
    };
  }

  if (isAnalyzableStatus(input.httpStatus)) {
    return {
      ...labels('no_php_evidence'),
      summary: NO_EVIDENCE_SUMMARY,
      ...fields,
      phpIndicators: analysis.indicators,
      technologies: analysis.technologies,
      findings: [
        finding('inconclusive', 'No reliable public PHP indicator', NO_EVIDENCE_SUMMARY),
      ],
      evidence: analysis.evidence,
      limitations: withCommonLimitations([
        'PHP can be installed with version headers removed. Absence of a header is not proof that PHP is absent or patched.',
        ...transport,
      ]),
      remediation: [
        'If this site uses PHP, check the runtime that serves web requests rather than relying on hidden response headers.',
        `Compare that runtime with ${SUPPORTED_FIXED_VERSION_LIST}. End-of-life branches did not receive an upstream fix.`,
        'Confirm PHP-FPM, Apache, CGI, and embedded PHP separately. A quiet HTTP header does not show which handler is patched.',
        'Review the official PHP advisories with your hosting provider if the server is managed for you.',
        'Retest after any change. This result does not mean the website was proven unaffected.',
      ],
    };
  }

  const summary = input.failureMessage ?? SAFE_MESSAGES.generic;
  return {
    ...labels('inconclusive'),
    summary,
    ...fields,
    phpIndicators: analysis.indicators,
    technologies: analysis.technologies,
    findings: [finding('inconclusive', 'Scan inconclusive', summary)],
    evidence: analysis.evidence.length > 0
      ? analysis.evidence
      : [evidence('request', 'Public response', 'not_observable', summary)],
    limitations: withCommonLimitations([summary, ...transport]),
    remediation: inconclusiveRemediation(),
  };
}
